import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export function StatCard({ title, value, icon: Icon, trend, trendValue, color = 'blue', compact = false }) {
  const colorClasses = {
    blue: 'text-blue-600 bg-blue-50',
    green: 'text-green-600 bg-green-50',
    red: 'text-red-600 bg-red-50',
    yellow: 'text-yellow-600 bg-yellow-50',
    purple: 'text-purple-600 bg-purple-50',
    indigo: 'text-indigo-600 bg-indigo-50'
  };

  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  const getTrendColor = () => {
    if (trend === 'up') return 'text-green-600';
    if (trend === 'down') return 'text-red-600';
    return 'text-gray-400';
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className={`flex flex-row items-center justify-between space-y-0 ${compact ? 'pb-1' : 'pb-2'}`}>
        <CardTitle className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-600`}>
          {title}
        </CardTitle>
        <div className={`${compact ? 'p-1' : 'p-2'} rounded-full ${colorClasses[color]}`}>
          <Icon className={`${compact ? 'h-3 w-3' : 'h-4 w-4'}`} />
        </div>
      </CardHeader>
      <CardContent className={compact ? 'pt-0' : ''}>
        <div className={`${compact ? 'text-lg' : 'text-2xl'} font-bold text-gray-900`}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {trendValue && (
          <div className="flex items-center mt-1">
            {getTrendIcon()}
            <span className={`text-xs ml-1 ${getTrendColor()}`}>
              {trendValue}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function UserTierStats({ title, users, projectGroups, projects, tasks, color = 'blue', compact = false }) {
  const colorClasses = {
    blue: 'border-blue-200 bg-blue-50',
    green: 'border-green-200 bg-green-50',
    red: 'border-red-200 bg-red-50',
    yellow: 'border-yellow-200 bg-yellow-50',
    purple: 'border-purple-200 bg-purple-50'
  };

  return (
    <Card className={`border-2 ${colorClasses[color]}`}>
      <CardHeader className={compact ? 'pb-2' : 'pb-3'}>
        <CardTitle className={`${compact ? 'text-sm' : 'text-lg'} font-semibold text-gray-800`}>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className={compact ? 'space-y-2' : 'space-y-3'}>
        <div className="flex justify-between">
          <span className="text-xs text-gray-600">Users:</span>
          <span className={`${compact ? 'text-sm' : 'text-base'} font-semibold text-gray-900`}>{users.toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-xs text-gray-600">Project Groups:</span>
          <span className={`${compact ? 'text-sm' : 'text-base'} font-semibold text-gray-900`}>{projectGroups.toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-xs text-gray-600">Projects:</span>
          <span className={`${compact ? 'text-sm' : 'text-base'} font-semibold text-gray-900`}>{projects.toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-xs text-gray-600">Tasks:</span>
          <span className={`${compact ? 'text-sm' : 'text-base'} font-semibold text-gray-900`}>{tasks.toLocaleString()}</span>
        </div>
        {!compact && (
          <div className="pt-2 border-t">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-700">Avg per user:</span>
              <span className="text-sm text-gray-600">
                {users > 0 ? (projectGroups / users).toFixed(1) : '0'} PG, {' '}
                {users > 0 ? (projects / users).toFixed(1) : '0'} P, {' '}
                {users > 0 ? (tasks / users).toFixed(1) : '0'} T
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
